import React, { useState, useRef, useEffect, useMemo } from 'react';

interface Option {
  value: string | number;
  label: string;
  data?: any; // Additional data for the option
}

// Overload for single selection (backwards compatibility)
interface SingleSelectProps {
  options: Option[];
  value?: string | number;
  selectedLabelFallback?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  emptyMessage?: string;
  allowClear?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  disableLocalFilter?: boolean;
  onSearchChange?: (value: string) => void;
  onLoadMore?: () => void;
  multiple?: false;
}

// Overload for multiple selection
interface MultiSelectProps {
  options: Option[];
  value?: (string | number)[];
  selectedLabelFallback?: string;
  onChange: (value: (string | number)[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  emptyMessage?: string;
  allowClear?: boolean;
  loading?: boolean;
  hasMore?: boolean;
  disableLocalFilter?: boolean;
  onSearchChange?: (value: string) => void;
  onLoadMore?: () => void;
  multiple: true;
}

type SearchableDropdownProps = SingleSelectProps | MultiSelectProps;

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  selectedLabelFallback,
  onChange,
  placeholder = 'Search or select option...',
  className = '',
  disabled = false,
  error = false,
  emptyMessage = 'No options found',
  allowClear = true,
  loading = false,
  hasMore = false,
  disableLocalFilter = false,
  onSearchChange,
  onLoadMore,
  multiple = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState(''); // New state for search in multiple mode
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const loadingMoreRef = useRef(false);

  // Handle multiple vs single value
  const selectedValues = useMemo(() => {
    return multiple && Array.isArray(value) ? value : value ? [value] : [];
  }, [multiple, value]);

  // For single select, find the selected option
  const selectedOption = useMemo(() => {
    return !multiple && value
      ? options.find(
          (option) => option?.value?.toString() === value?.toString()
        )
      : null;
  }, [multiple, value, options]);

  // Update input value when selection changes or component mounts
  useEffect(() => {
    if (multiple) {
      // For multiple selection, we'll show tags, so keep input empty for search
      if (!isOpen) {
        setInputValue('');
        setSearchValue('');
      }
    } else {
      if (isOpen) {
        return;
      }

      if (selectedOption) {
        setInputValue(selectedOption.label);
      } else if (selectedLabelFallback && value) {
        setInputValue(selectedLabelFallback);
      } else {
        setInputValue('');
      }
    }
  }, [
    selectedValues,
    selectedOption,
    selectedLabelFallback,
    multiple,
    isOpen,
    value,
  ]);

  const filteredOptions = useMemo(() => {
    if (disableLocalFilter) {
      return options;
    }

    const searchTerm = multiple ? searchValue : inputValue;

    if (!searchTerm || (!multiple && selectedOption?.label === inputValue)) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [
    disableLocalFilter,
    inputValue,
    searchValue,
    options,
    selectedOption,
    multiple,
  ]);

  useEffect(() => {
    if (!loading) {
      loadingMoreRef.current = false;
    }
  }, [loading]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        // Reset input to selected value when clicking outside
        if (selectedOption) {
          setInputValue(selectedOption.label);
        } else if (selectedLabelFallback && value) {
          setInputValue(selectedLabelFallback);
        } else {
          setInputValue('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedOption, selectedLabelFallback, value]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleOptionSelect = (option: Option) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.includes(option.value);

      if (isSelected) {
        // Remove the option
        const newValues = currentValues.filter((v) => v !== option.value);
        (onChange as (value: (string | number)[]) => void)(newValues);
      } else {
        // Add the option
        const newValues = [...currentValues, option.value];
        (onChange as (value: (string | number)[]) => void)(newValues);
      }
      // Don't close dropdown for multiple selection
    } else {
      (onChange as (value: string) => void)(option.value as string);
      setInputValue(option.label);
      setIsOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiple) {
      (onChange as (value: (string | number)[]) => void)([]);
    } else {
      (onChange as (value: string) => void)('');
    }
    setIsOpen(false);
  };

  const handleRemoveTag = (
    valueToRemove: string | number,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (multiple && Array.isArray(value)) {
      const newValues = value.filter((v) => v !== valueToRemove);
      (onChange as (value: (string | number)[]) => void)(newValues);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (multiple) {
      setSearchValue(value);
      setInputValue(value);
    } else {
      setInputValue(value);
    }
    onSearchChange?.(value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      if (selectedOption) {
        setInputValue(selectedOption.label);
      } else {
        setInputValue('');
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length === 1) {
        handleOptionSelect(filteredOptions[0]);
      } else if (filteredOptions.length > 1) {
        // Find exact match
        const exactMatch = filteredOptions.find(
          (option) => option.label.toLowerCase() === inputValue.toLowerCase()
        );
        if (exactMatch) {
          handleOptionSelect(exactMatch);
        }
      }
    }
  };

  const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || loading || loadingMoreRef.current) {
      return;
    }

    const target = e.currentTarget;
    const threshold = 24;
    const isNearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

    if (isNearBottom) {
      loadingMoreRef.current = true;
      onLoadMore();
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Search Input Field */}
      <div className="relative">
        {multiple ? (
          // Multiple selection with tags
          <div
            className={`
            w-full min-h-[2.5rem] px-3 py-1 border rounded-md
            focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 transition-colors
            ${error ? 'border-red-500' : 'border-gray-300'}
            ${
              disabled
                ? 'bg-gray-200 cursor-not-allowed'
                : 'bg-white hover:border-gray-400'
            }
            ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
            flex flex-wrap items-center gap-1
          `}
          >
            {/* Display selected tags */}
            {selectedValues.map((val, index) => {
              // Ensure val is a string or number for display
              const displayVal = Array.isArray(val) ? val[0] : val;
              const option = options.find((opt) => opt.value === displayVal);
              const label = option?.label || displayVal?.toString() || '';

              return (
                <span
                  key={`${displayVal}-${index}`}
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                >
                  {label}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) =>
                        handleRemoveTag(displayVal as string | number, e)
                      }
                      className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  )}
                </span>
              );
            })}

            {/* Search input on the right */}
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              placeholder={selectedValues.length === 0 ? placeholder : ''}
              disabled={disabled}
              className="flex-1 min-w-[120px] outline-none bg-transparent text-base md:text-sm"
              style={{ minWidth: selectedValues.length > 0 ? '120px' : 'auto' }}
            />
          </div>
        ) : (
          // Single selection (original input)
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={`
              w-full px-3 py-2 pr-10 border rounded-md text-base md:text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors
              ${error ? 'border-red-500' : 'border-gray-300'}
              ${
                disabled
                  ? 'bg-gray-200 cursor-not-allowed'
                  : 'bg-white hover:border-gray-400'
              }
              ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
            `}
          />
        )}

        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {/* Clear Button */}
          {allowClear &&
            (multiple ? selectedValues.length > 0 : selectedOption) &&
            !disabled && (
              <button
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                type="button"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          {/* Dropdown Icon */}
          <button
            onClick={handleToggle}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <i
              className={`ri-arrow-down-s-line transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            ></i>
          </button>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
          onScroll={handleDropdownScroll}
        >
          {filteredOptions.length > 0 ? (
            <>
              {filteredOptions.map((option) => {
                const isSelected = multiple
                  ? selectedValues.includes(option.value)
                  : selectedOption?.value === option.value;

                return (
                  <div
                    key={option.value}
                    onClick={() => handleOptionSelect(option)}
                    className={`
                      w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer flex items-center
                      ${
                        isSelected
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-900'
                      }
                    `}
                  >
                    {multiple && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="mr-2 text-blue-600 focus:ring-blue-500"
                      />
                    )}
                    <span>{option.label}</span>
                  </div>
                );
              })}
              {loading && (
                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                  Loading...
                </div>
              )}
            </>
          ) : (
            <>
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                {loading ? 'Loading...' : emptyMessage}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
